import { motion } from 'framer-motion';
import { 
  Compass, Buildings, FilmStrip, Lightning,
  Mountains, Warehouse, TreeStructure, Drop,
  House, Ruler, Cube, Camera, Drone, Robot
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function Industry() {
  const { t } = useLanguage();

  const industries = [
    {
      id: 'survey',
      title: t('industry.surveyGeo'),
      description: t('industry.surveyGeoDesc'),
      icon: Compass,
      color: 'text-blue-400',
      applications: [
        { icon: Mountains, name: 'Survei Topografi', nameEn: 'Topographic Survey', desc: 'Pemetaan permukaan tanah dengan point cloud density tinggi', descEn: 'Surface mapping with high point cloud density' },
        { icon: Warehouse, name: 'Terowongan & Utilitas', nameEn: 'Tunnels & Utilities', desc: 'Scan interior terowongan dan koridor utilitas', descEn: 'Interior scanning of tunnels and utility corridors' },
        { icon: TreeStructure, name: 'Perhitungan Volume Stockpile', nameEn: 'Stockpile Volume Calculation', desc: 'Akurasi tinggi untuk manajemen inventori material', descEn: 'High accuracy for material inventory management' },
        { icon: Buildings, name: 'Ruang Bawah Tanah Perkotaan', nameEn: 'Urban Underground Space', desc: 'Mapping infrastruktur bawah tanah', descEn: 'Underground infrastructure mapping' },
        { icon: Drop, name: 'Manajemen Kehutanan', nameEn: 'Forestry Management', desc: 'Inventarisasi hutan dan analisis biomassa', descEn: 'Forest inventory and biomass analysis' },
      ]
    },
    {
      id: 'aec',
      title: t('industry.aec'),
      description: t('industry.aecDesc'),
      icon: Buildings,
      color: 'text-emerald-400',
      applications: [
        { icon: Ruler, name: 'Pengukuran Fasad', nameEn: 'Facade Measurement', desc: 'Dokumentasi detail eksterior bangunan', descEn: 'Detailed building exterior documentation' },
        { icon: House, name: 'Survei Real Estate', nameEn: 'Real Estate Survey', desc: 'Floor plan dan model 3D untuk properti', descEn: 'Floor plans and 3D models for properties' },
        { icon: Cube, name: 'Reverse BIM Modeling', nameEn: 'Reverse BIM Modeling', desc: 'Konversi point cloud ke model BIM', descEn: 'Point cloud to BIM model conversion' },
        { icon: Camera, name: 'Manajemen Progress Konstruksi', nameEn: 'Construction Progress Management', desc: 'Tracking progress dengan perbandingan 4D BIM', descEn: 'Progress tracking with 4D BIM comparison' },
      ]
    },
    {
      id: 'film',
      title: t('industry.film'),
      description: t('industry.filmDesc'),
      icon: FilmStrip,
      color: 'text-purple-400',
      applications: [
        { icon: Camera, name: 'Location Scouting & Pre-Vis', nameEn: 'Location Scouting & Pre-Vis', desc: 'Virtual scout lokasi dengan detail akurat', descEn: 'Virtual location scouting with accurate details' },
        { icon: Cube, name: 'AR/VR Content', nameEn: 'AR/VR Content', desc: 'Asset 3D realistis untuk pengalaman immersive', descEn: 'Realistic 3D assets for immersive experiences' },
      ]
    },
    {
      id: 'energy',
      title: t('industry.energy'),
      description: t('industry.energyDesc'),
      icon: Lightning,
      color: 'text-amber-400',
      applications: [
        { icon: Buildings, name: 'Digital Twin Fasilitas', nameEn: 'Facility Digital Twin', desc: 'Replika digital untuk maintenance dan planning', descEn: 'Digital replica for maintenance and planning' },
        { icon: Drone, name: 'Inspeksi Drone/Robot', nameEn: 'Drone/Robot Inspection', desc: 'Integrasi dengan platform inspeksi otomatis', descEn: 'Integration with automated inspection platforms' },
        { icon: Robot, name: 'Smart Mining', nameEn: 'Smart Mining', desc: 'Operasi tambang berbasis data 3D real-time', descEn: 'Mining operations based on real-time 3D data' },
        { icon: Drop, name: 'Manajemen Sumber Daya Air', nameEn: 'Water Resource Management', desc: 'Monitoring dan modeling infrastruktur air', descEn: 'Water infrastructure monitoring and modeling' },
      ]
    },
  ];

  const { language } = useLanguage();

  return (
    <div className="min-h-screen pt-20" data-testid="industry-page">
      {/* Header */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('industry.coverage')}</p>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="industry-title">
              {t('industry.title')}
            </h1>
            <p className="text-xgrids-text-secondary text-lg max-w-2xl">
              {t('industry.desc')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {industries.map((industry, idx) => (
            <motion.div
              key={industry.id}
              id={industry.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="mb-20 last:mb-0"
              data-testid={`industry-section-${industry.id}`}
            >
              {/* Industry Header */}
              <div className="flex items-start gap-6 mb-10">
                <div className={`p-4 bg-xgrids-bg-surface border border-white/10 rounded-sm ${industry.color}`}>
                  <industry.icon size={32} weight="duotone" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
                    {industry.title}
                  </h2>
                  <p className="text-xgrids-text-secondary">{industry.description}</p>
                </div>
              </div>

              {/* Applications Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {industry.applications.map((app, appIdx) => (
                  <motion.div
                    key={app.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: appIdx * 0.05 }}
                    className="card-hover bg-xgrids-bg-surface p-6 rounded-sm group"
                  >
                    <app.icon 
                      size={28} 
                      weight="duotone" 
                      className={`${industry.color} mb-4 group-hover:scale-110 transition-transform`} 
                    />
                    <h3 className="font-heading font-bold text-white mb-2">
                      {language === 'en' ? app.nameEn : app.name}
                    </h3>
                    <p className="text-xgrids-text-secondary text-sm">
                      {language === 'en' ? app.descEn : app.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-6">
              {t('industry.notFound')}
            </h2>
            <p className="text-xgrids-text-secondary mb-8">
              {t('industry.notFoundDesc')}
            </p>
            <Link
              to="/tentang#contact"
              className="btn-primary rounded-none inline-flex items-center gap-2"
              data-testid="industry-cta-btn"
            >
              {t('industry.freeConsultation')}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
